import { useState } from "react";
import { Check, Copy, ChevronDown, MessageSquare, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  generateReceipt,
  SAMPLE_TRANSACTIONS,
  RECEIPT_STORE_TYPE_LABELS,
  type StoreType,
} from "@/lib/receipt";

const STORE_TYPES = Object.keys(RECEIPT_STORE_TYPE_LABELS) as StoreType[];

const TYPE_META: Record<StoreType, { color: string; icon: string }> = {
  FASHION_DESIGNER: { color: "from-pink-500 to-rose-600",   icon: "🧵" },
  FOOD_RESTAURANT:  { color: "from-orange-500 to-amber-600", icon: "🍲" },
  PHARMACY:         { color: "from-emerald-500 to-teal-600", icon: "💊" },
  GADGET_REPAIR:    { color: "from-blue-500 to-indigo-600",  icon: "📱" },
};

export default function ReceiptPreview() {
  const [activeType, setActiveType] = useState<StoreType>("FASHION_DESIGNER");
  const [copied, setCopied] = useState(false);

  const transaction = SAMPLE_TRANSACTIONS[activeType];
  const receipt = generateReceipt(transaction, activeType);

  function handleCopy() {
    navigator.clipboard.writeText(receipt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const meta = TYPE_META[activeType];

  return (
    <div className="min-h-screen bg-slate-950 text-white py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-4 py-1.5 text-sm text-slate-300 mb-2">
            <MessageSquare className="w-3.5 h-3.5" />
            WhatsApp / SMS Receipt Generator
          </div>
          <h1 className="text-3xl font-bold">Dynamic Receipt Preview</h1>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            Select a business type to preview the auto-generated receipt message that gets sent to your customers.
          </p>
        </div>

        {/* Store type selector — pills */}
        <div className="flex flex-wrap gap-2 justify-center">
          {STORE_TYPES.map((type) => {
            const m = TYPE_META[type];
            const isActive = activeType === type;
            return (
              <button
                key={type}
                onClick={() => { setActiveType(type); setCopied(false); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                  isActive
                    ? `bg-gradient-to-r ${m.color} border-transparent text-white shadow-lg`
                    : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <span>{m.icon}</span>
                {RECEIPT_STORE_TYPE_LABELS[type]}
              </button>
            );
          })}
        </div>

        {/* Active card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">

          {/* Card header */}
          <div className={`bg-gradient-to-r ${meta.color} px-6 py-4 flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{meta.icon}</span>
              <div>
                <div className="font-semibold text-white">{RECEIPT_STORE_TYPE_LABELS[activeType]}</div>
                <div className="text-xs text-white/70">{transaction.storeName} · {transaction.storePhone}</div>
              </div>
            </div>
            <div className="text-right text-xs text-white/70">
              <div>Customer: <span className="text-white font-medium">{transaction.customerName}</span></div>
              <div>Ref: <span className="text-white font-medium">{transaction.id}</span></div>
            </div>
          </div>

          {/* Receipt text */}
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide font-medium">
              <FileText className="w-3.5 h-3.5" />
              Generated receipt message
            </div>

            <div className="relative">
              <textarea
                readOnly
                value={receipt}
                rows={receipt.split("\n").length + 1}
                className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-4 text-sm text-slate-200 font-mono leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-white/20"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-4 pt-1">
              <p className="text-xs text-slate-500">
                Ready to send via WhatsApp, SMS, or Termii
              </p>
              <Button
                onClick={handleCopy}
                className={`flex items-center gap-2 transition-all duration-200 ${
                  copied
                    ? "bg-emerald-600 hover:bg-emerald-600 text-white"
                    : "bg-violet-600 hover:bg-violet-700 text-white"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy to Clipboard
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Transaction data snapshot */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide font-medium flex items-center gap-2">
            <ChevronDown className="w-3.5 h-3.5" />
            Sample transaction object powering this receipt
          </div>
          <pre className="text-xs text-emerald-400 font-mono leading-relaxed overflow-x-auto bg-slate-900 rounded-xl p-4 border border-white/5">
{JSON.stringify(transaction, null, 2)}
          </pre>
        </div>

        <p className="text-center text-xs text-slate-600 pb-4">
          Integrate <code className="text-slate-400">generateReceipt(transaction, storeType)</code> from <code className="text-slate-400">@/lib/receipt</code> anywhere in your POS flow.
        </p>
      </div>
    </div>
  );
}
